// src/pages/ExamPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QuestionTimer from '../components/QuestionTimer';
import './ExamPage.css';

const TOTAL_QUESTIONS = 20;
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'];

const ExamPage = () => {
  const { examCode } = useParams();
  const navigate = useNavigate();
  const [examData, setExamData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeSpent, setTimeSpent] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tempoRestante, setTempoRestante] = useState(null);

  const handleTimeUp = useCallback(() => {
    axios.post(`${process.env.REACT_APP_API_URL}/exam/${examCode}/finish`);
    navigate(`/result/${examCode}`);
  }, [examCode, navigate]);

  useEffect(() => {
    const fetchExam = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/exam/${examCode}`);
        // Se não há mais questões pendentes, a prova já foi respondida por completo:
        // finaliza automaticamente e envia o candidato para o resultado.
        if (!response.data.questions || response.data.questions.length === 0) {
          await axios.post(`${process.env.REACT_APP_API_URL}/exam/${examCode}/finish`);
          navigate(`/result/${examCode}`);
          return;
        }
        setTempoRestante(response.data.remaining_time);
        setExamData(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Erro ao carregar a prova');
      } finally {
        setIsLoading(false);
      }
    };

    fetchExam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examCode]);

  const isTimerReady = tempoRestante !== null;

  useEffect(() => {
    if (!isTimerReady) return;
    const interval = setInterval(() => {
      setTempoRestante(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isTimerReady, handleTimeUp]);

  useEffect(() => {
    setTimeSpent(prev => prev + 1);
  }, [tempoRestante]);

  // Impede que o candidato saia acidentalmente da página durante a prova
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleOptionSelect = useCallback((option) => {
    setSelectedOption(option);
  }, []);

  const handleSubmitAnswer = async () => {
    if (!selectedOption) {
      alert('Por favor, selecione uma opção');
      return;
    }

    const isLastQuestion = currentQuestionIndex === examData.questions.length - 1;
    if (isLastQuestion && !window.confirm('Esta é a última questão. Deseja finalizar a prova?')) {
      return;
    }

    setIsSubmitting(true);

    try {
      const currentQuestion = examData.questions[currentQuestionIndex];
      await axios.post(`${process.env.REACT_APP_API_URL}/exam/${examCode}/answer`, {
        question_id: currentQuestion.id,
        answer: selectedOption,
        time_spent: timeSpent
      });

      if (!isLastQuestion) {
        setCurrentQuestionIndex(prevIndex => prevIndex + 1);
        setSelectedOption('');
        setTimeSpent(0);
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/exam/${examCode}/finish`);
        navigate(`/result/${examCode}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao submeter resposta');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Atalhos de teclado: teclas A-E selecionam a opção, Enter confirma
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toUpperCase();
      if (OPTION_LETTERS.includes(key)) {
        handleOptionSelect(key);
      } else if (e.key === 'Enter') {
        handleSubmitAnswer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, currentQuestionIndex, examData]);

  if (isLoading) return <div className="loading">Carregando prova...</div>;
  if (error) return <div className="error">{error}</div>;

  const currentQuestion = examData?.questions[currentQuestionIndex];
  if (!currentQuestion) return <div className="loading">Carregando prova...</div>;

  const progressPercentage = ((currentQuestion.ordem - 1) / TOTAL_QUESTIONS) * 100;

  return (
    <div className="exam-container">
      <div className="exam-header">
        <div className="exam-progress-info">
          <h2>Questão {currentQuestion.ordem} de {TOTAL_QUESTIONS}</h2>
          <div className="exam-progress-bar">
            <div className="exam-progress-fill" style={{ width: `${progressPercentage}%` }}></div>
          </div>
        </div>
        <QuestionTimer 
          maxTime={tempoRestante}
        />
      </div>

      <div className="question-container">
        <p className="question-text">{currentQuestion?.enunciado}</p>

        <div className="options-container">
          {OPTION_LETTERS.map(letter => (
            <div 
              key={letter}
              className={`option ${selectedOption === letter ? 'selected' : ''}`}
              onClick={() => handleOptionSelect(letter)}
            >
              <span className="option-letter">{letter}</span>
              <span className="option-text">{currentQuestion[`opcao_${letter.toLowerCase()}`]}</span>
            </div>
          ))}
        </div>

        <button 
          className="submit-btn"
          onClick={handleSubmitAnswer}
          disabled={!selectedOption || isSubmitting}
        >
          {isSubmitting ? 'Enviando...' : currentQuestion.ordem === TOTAL_QUESTIONS ? 'Finalizar Prova' : 'Próxima Questão'}
        </button>
      </div>
    </div>
  );
};

export default ExamPage;

